/**
 * ChecklistItem Service
 * 
 * Servizio per interagire con le API ChecklistItem del backend
 * Gestisce tutte le operazioni CRUD e le funzionalità avanzate delle checklist
 */

import axiosInstance from '../lib/axios-client';
import {
  ChecklistItemDto,
  CreateChecklistItemDto,
  UpdateChecklistItemDto,
  ChecklistProgressDto,
  ChecklistStatisticsDto,
  ChecklistTaskStatisticsDto,
  ChecklistItemSearchDto,
  ChecklistItemDetailDto,
  ChecklistItemBatchOperationDto,
  ChecklistTemplateDto,
  ApplyChecklistTemplateDto,
  CompleteChecklistItemDto,
  ReorderChecklistItemsDto,
  ChecklistApiResponse,
  ChecklistPagedResponse,
  ChecklistSearchParams,
  UserSummaryDto,
  ProjectTaskSummaryDto
} from '../types/checklist-types';

const API_URL = '/api/ChecklistItem';

// ================== MAPPING FUNCTIONS ==================

/**
 * Mappa i dati del backend al formato del frontend
 */
const mapDtoToChecklistItem = (dto: any): ChecklistItemDto => {
  return {
    id: dto.id,
    title: dto.title,
    description: dto.description || undefined,
    isCompleted: dto.isCompleted,
    completedAt: dto.completedAt ? new Date(dto.completedAt) : undefined,
    completedBy: dto.completedBy || undefined,
    order: dto.order,
    createdAt: new Date(dto.createdAt),
    updatedAt: dto.updatedAt ? new Date(dto.updatedAt) : undefined,
    
    // Riferimenti
    taskId: dto.taskId,
    completedByUserId: dto.completedByUserId || undefined,
    
    // Informazioni UI
    taskTitle: dto.taskTitle || undefined,
    dueDate: dto.dueDate ? new Date(dto.dueDate) : undefined,
    assignedTo: dto.assignedTo || undefined,
    isOverdue: dto.isOverdue || false,
    timeSpent: dto.timeSpent || undefined,
    
    // Relazioni
    projectTask: dto.projectTask ? mapToProjectTaskSummary(dto.projectTask) : undefined,
    completedByUser: dto.completedByUser ? mapToUserSummary(dto.completedByUser) : undefined
  };
};

/**
 * Mappa i dati del frontend al formato atteso dal backend
 */
const mapChecklistItemToDto = (item: CreateChecklistItemDto | UpdateChecklistItemDto): any => {
  return {
    title: item.title,
    description: item.description || null,
    order: 'order' in item ? item.order : 0,
    isCompleted: 'isCompleted' in item ? item.isCompleted : false,
    dueDate: item.dueDate?.toISOString() || null,
    assignedTo: item.assignedTo || null
  };
};

/**
 * Mappa UserSummaryDto dal backend
 */
const mapToUserSummary = (dto: any): UserSummaryDto => ({
  id: dto.id,
  fullName: dto.fullName || dto.name || 'Unknown',
  email: dto.email || ''
});

/**
 * Mappa ProjectTaskSummaryDto dal backend
 */
const mapToProjectTaskSummary = (dto: any): ProjectTaskSummaryDto => ({
  id: dto.id,
  name: dto.name || dto.title || 'Unknown Task',
  status: dto.status || 'Unknown',
  dueDate: dto.dueDate ? new Date(dto.dueDate) : undefined
});

/**
 * Mappa ChecklistProgressDto dal backend
 */
const mapToChecklistProgress = (dto: any): ChecklistProgressDto => ({
  taskId: dto.taskId,
  taskTitle: dto.taskTitle || undefined,
  totalItems: dto.totalItems,
  completedItems: dto.completedItems,
  pendingItems: dto.pendingItems,
  completionPercentage: dto.completionPercentage,
  lastUpdated: dto.lastUpdated ? new Date(dto.lastUpdated) : undefined,
  isFullyCompleted: dto.isFullyCompleted,
  hasItems: dto.hasItems,
  lastCompletedByUser: dto.lastCompletedByUser ? mapToUserSummary(dto.lastCompletedByUser) : undefined,
  items: dto.items?.map((item: any) => ({
    id: item.id,
    title: item.title,
    isCompleted: item.isCompleted,
    completedAt: item.completedAt ? new Date(item.completedAt) : undefined,
    order: item.order,
    completedByUser: item.completedByUser ? mapToUserSummary(item.completedByUser) : undefined
  })) || []
});

// ================== SERVICE IMPLEMENTATION ==================

export const checklistItemService = {
  
  // ================== BASIC CRUD OPERATIONS ==================
  
  /**
   * Ottiene tutti gli elementi di checklist con paginazione
   */
  getAll: async (params?: ChecklistSearchParams): Promise<ChecklistPagedResponse<ChecklistItemDto>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
      if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      
      const response = await axiosInstance.get(`${API_URL}?${queryParams.toString()}`);
      
      return {
        items: response.data.items.map(mapDtoToChecklistItem),
        totalCount: response.data.totalCount,
        pageNumber: response.data.pageNumber,
        pageSize: response.data.pageSize,
        totalPages: response.data.totalPages,
        hasNextPage: response.data.hasNextPage,
        hasPreviousPage: response.data.hasPreviousPage
      };
    } catch (error) {
      console.error('Errore nel recupero delle checklist:', error);
      throw error;
    }
  },
  
  /**
   * Ottiene un elemento specifico per ID
   */
  getById: async (id: number): Promise<ChecklistItemDto> => {
    try {
      const response = await axiosInstance.get(`${API_URL}/${id}`);
      return mapDtoToChecklistItem(response.data);
    } catch (error) {
      console.error(`Errore nel recupero dell'elemento checklist ${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Ottiene tutti gli elementi di una checklist per task
   */
  getByTaskId: async (taskId: number): Promise<ChecklistItemDto[]> => {
    try {
      const response = await axiosInstance.get(`${API_URL}/by-task/${taskId}`);
      
      // Gestione risposta paginata - supporta sia array diretto che oggetto paginato
      let checklistItems = [];
      
      if (Array.isArray(response.data)) {
        // Risposta diretta come array
        checklistItems = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        // Risposta paginata con struttura { data: [...], totalCount: x, page: y }
        checklistItems = response.data.data;
      } else if (response.data && response.data.items && Array.isArray(response.data.items)) {
        // Altra possibile struttura paginata { items: [...], totalCount: x }
        checklistItems = response.data.items;
      } else {
        // Fallback: array vuoto
        checklistItems = [];
        console.warn('Formato risposta API checklist non riconosciuto:', response.data);
      }
      
      return checklistItems.map(mapDtoToChecklistItem);
    } catch (error) {
      console.error(`Errore nel recupero della checklist per il task ${taskId}:`, error);
      throw error;
    }
  },
  
  /**
   * Crea un nuovo elemento checklist
   */
  create: async (taskId: number, item: CreateChecklistItemDto): Promise<ChecklistItemDto> => {
    try {
      const itemDto = mapChecklistItemToDto(item);
      const response = await axiosInstance.post(`${API_URL}/add-to-task/${taskId}`, itemDto);
      return mapDtoToChecklistItem(response.data.data);
    } catch (error) {
      console.error('Errore nella creazione dell\'elemento checklist:', error);
      throw error;
    }
  },
  
  /**
   * Aggiorna un elemento checklist
   */
  update: async (id: number, item: UpdateChecklistItemDto): Promise<ChecklistItemDto> => {
    try {
      const itemDto = mapChecklistItemToDto(item);
      const response = await axiosInstance.put(`${API_URL}/${id}`, itemDto);
      return mapDtoToChecklistItem(response.data);
    } catch (error) {
      console.error(`Errore nell'aggiornamento dell'elemento checklist ${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Elimina un elemento checklist
   */
  delete: async (id: number): Promise<void> => {
    try {
      await axiosInstance.delete(`${API_URL}/${id}`);
    } catch (error) {
      console.error(`Errore nell'eliminazione dell'elemento checklist ${id}:`, error);
      throw error;
    }
  },
  
  // ================== COMPLETION OPERATIONS ==================
  
  /**
   * Completa un elemento checklist
   */
  complete: async (id: number, completionData: CompleteChecklistItemDto): Promise<void> => {
    try {
      await axiosInstance.post(`${API_URL}/${id}/complete`, completionData);
    } catch (error) {
      console.error(`Errore nel completamento dell'elemento checklist ${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Annulla il completamento di un elemento checklist
   */
  uncomplete: async (id: number): Promise<void> => {
    try {
      await axiosInstance.post(`${API_URL}/${id}/uncomplete`);
    } catch (error) {
      console.error(`Errore nell'annullamento del completamento dell'elemento checklist ${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Toggle dello stato di completamento
   */
  toggleComplete: async (id: number, userId: number): Promise<ChecklistItemDto> => {
    try {
      const item = await checklistItemService.getById(id);
      if (item.isCompleted) {
        await checklistItemService.uncomplete(id);
      } else {
        await checklistItemService.complete(id, { completedByUserId: userId });
      }
      // Ricarica l'elemento aggiornato
      return await checklistItemService.getById(id);
    } catch (error) {
      console.error(`Errore nel toggle dello stato dell'elemento checklist ${id}:`, error);
      throw error;
    }
  },
  
  // ================== ORDERING OPERATIONS ==================
  
  /**
   * Riordina gli elementi di una checklist
   */
  reorder: async (reorderData: ReorderChecklistItemsDto): Promise<void> => {
    try {
      await axiosInstance.post(`${API_URL}/reorder`, reorderData);
    } catch (error) {
      console.error('Errore nel riordinamento degli elementi checklist:', error);
      throw error;
    }
  },
  
  // ================== SEARCH & FILTER OPERATIONS ==================
  
  /**
   * Cerca elementi checklist
   */
  search: async (searchTerm: string, taskId?: number): Promise<ChecklistItemSearchDto[]> => {
    try {
      const params = new URLSearchParams();
      params.append('searchTerm', searchTerm);
      if (taskId) params.append('taskId', taskId.toString());
      
      const response = await axiosInstance.get(`${API_URL}/search?${params.toString()}`);
      return response.data.map((item: any) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        isCompleted: item.isCompleted,
        taskId: item.taskId,
        taskTitle: item.taskTitle,
        assignedTo: item.assignedTo,
        dueDate: item.dueDate ? new Date(item.dueDate) : undefined,
        isOverdue: item.isOverdue,
        matchHighlight: item.matchHighlight
      }));
    } catch (error) {
      console.error('Errore nella ricerca degli elementi checklist:', error);
      throw error;
    }
  },
  
  /**
   * Ottiene elementi completati
   */
  getCompleted: async (pageNumber = 1, pageSize = 20): Promise<ChecklistPagedResponse<ChecklistItemDto>> => {
    try {
      const response = await axiosInstance.get(`${API_URL}/completed?pageNumber=${pageNumber}&pageSize=${pageSize}`);
      return {
        items: response.data.items.map(mapDtoToChecklistItem),
        totalCount: response.data.totalCount,
        pageNumber: response.data.pageNumber,
        pageSize: response.data.pageSize,
        totalPages: response.data.totalPages,
        hasNextPage: response.data.hasNextPage,
        hasPreviousPage: response.data.hasPreviousPage
      };
    } catch (error) {
      console.error('Errore nel recupero degli elementi completati:', error);
      throw error;
    }
  },
  
  /**
   * Ottiene elementi in scadenza
   */
  getPending: async (pageNumber = 1, pageSize = 20): Promise<ChecklistPagedResponse<ChecklistItemDto>> => {
    try {
      const response = await axiosInstance.get(`${API_URL}/pending?pageNumber=${pageNumber}&pageSize=${pageSize}`);
      return {
        items: response.data.items.map(mapDtoToChecklistItem),
        totalCount: response.data.totalCount,
        pageNumber: response.data.pageNumber,
        pageSize: response.data.pageSize,
        totalPages: response.data.totalPages,
        hasNextPage: response.data.hasNextPage,
        hasPreviousPage: response.data.hasPreviousPage
      };
    } catch (error) {
      console.error('Errore nel recupero degli elementi in sospeso:', error);
      throw error;
    }
  },
  
  /**
   * Ottiene elementi scaduti
   */
  getOverdue: async (pageNumber = 1, pageSize = 20): Promise<ChecklistPagedResponse<ChecklistItemDto>> => {
    try {
      const response = await axiosInstance.get(`${API_URL}/overdue?pageNumber=${pageNumber}&pageSize=${pageSize}`);
      return {
        items: response.data.items.map(mapDtoToChecklistItem),
        totalCount: response.data.totalCount,
        pageNumber: response.data.pageNumber,
        pageSize: response.data.pageSize,
        totalPages: response.data.totalPages,
        hasNextPage: response.data.hasNextPage,
        hasPreviousPage: response.data.hasPreviousPage
      };
    } catch (error) {
      console.error('Errore nel recupero degli elementi scaduti:', error);
      throw error;
    }
  },
  
  // ================== STATISTICS & PROGRESS ==================
  
  /**
   * Ottiene il progresso di una checklist per task
   */
  getProgress: async (taskId: number): Promise<ChecklistProgressDto> => {
    try {
      const response = await axiosInstance.get(`${API_URL}/progress/${taskId}`);
      return mapToChecklistProgress(response.data);
    } catch (error) {
      console.error(`Errore nel recupero del progresso per il task ${taskId}:`, error);
      throw error;
    }
  },
  
  /**
   * Ottiene statistiche generali delle checklist
   */
  getStatistics: async (): Promise<ChecklistStatisticsDto> => {
    try {
      const response = await axiosInstance.get(`${API_URL}/statistics`);
      return {
        totalItems: response.data.totalItems,
        completedItems: response.data.completedItems,
        pendingItems: response.data.pendingItems,
        overdueItems: response.data.overdueItems,
        overallCompletionPercentage: response.data.overallCompletionPercentage,
        tasksWithChecklists: response.data.tasksWithChecklists,
        tasksWithCompletedChecklists: response.data.tasksWithCompletedChecklists,
        taskCompletionPercentage: response.data.taskCompletionPercentage,
        earliestCreated: response.data.earliestCreated ? new Date(response.data.earliestCreated) : undefined,
        latestCompleted: response.data.latestCompleted ? new Date(response.data.latestCompleted) : undefined,
        itemsCompletedToday: response.data.itemsCompletedToday,
        itemsCompletedThisWeek: response.data.itemsCompletedThisWeek,
        itemsCompletedThisMonth: response.data.itemsCompletedThisMonth,
        topUsers: response.data.topUsers || [],
        mostActiveTasks: response.data.mostActiveTasks || []
      };
    } catch (error) {
      console.error('Errore nel recupero delle statistiche checklist:', error);
      throw error;
    }
  },
  
  /**
   * Ottiene statistiche per un task specifico
   */
  getTaskStatistics: async (taskId: number): Promise<ChecklistTaskStatisticsDto> => {
    try {
      const response = await axiosInstance.get(`${API_URL}/statistics/task/${taskId}`);
      return {
        taskId: response.data.taskId,
        taskTitle: response.data.taskTitle,
        totalItems: response.data.totalItems,
        completedItems: response.data.completedItems,
        pendingItems: response.data.pendingItems,
        overdueItems: response.data.overdueItems,
        completionPercentage: response.data.completionPercentage,
        firstItemCreated: response.data.firstItemCreated ? new Date(response.data.firstItemCreated) : undefined,
        lastItemCompleted: response.data.lastItemCompleted ? new Date(response.data.lastItemCompleted) : undefined,
        assignedUsers: response.data.assignedUsers || [],
        averageCompletionTime: response.data.averageCompletionTime,
        recentlyCompleted: response.data.recentlyCompleted?.map(mapDtoToChecklistItem) || [],
        overdueItems_Detail: response.data.overdueItems_Detail?.map(mapDtoToChecklistItem) || []
      };
    } catch (error) {
      console.error(`Errore nel recupero delle statistiche per il task ${taskId}:`, error);
      throw error;
    }
  },
  
  // ================== BATCH OPERATIONS ==================
  
  /**
   * Esegue operazioni in batch su più elementi
   */
  bulkOperation: async (operation: ChecklistItemBatchOperationDto): Promise<void> => {
    try {
      await axiosInstance.post(`${API_URL}/bulk-operation`, operation);
    } catch (error) {
      console.error('Errore nell\'operazione bulk:', error);
      throw error;
    }
  },
  
  /**
   * Completa più elementi in batch
   */
  bulkComplete: async (itemIds: number[], userId: number): Promise<void> => {
    return checklistItemService.bulkOperation({
      itemIds,
      operation: 'complete',
      value: userId.toString()
    });
  },
  
  /**
   * Annulla il completamento di più elementi in batch
   */
  bulkUncomplete: async (itemIds: number[]): Promise<void> => {
    return checklistItemService.bulkOperation({
      itemIds,
      operation: 'uncomplete'
    });
  },
  
  /**
   * Elimina più elementi in batch
   */
  bulkDelete: async (itemIds: number[]): Promise<void> => {
    return checklistItemService.bulkOperation({
      itemIds,
      operation: 'delete'
    });
  },
  
  // ================== TEMPLATES ==================
  
  /**
   * Ottiene tutti i template disponibili
   */
  getTemplates: async (): Promise<ChecklistTemplateDto[]> => {
    try {
      const response = await axiosInstance.get(`${API_URL}/templates`);
      return response.data.map((template: any) => ({
        id: template.id,
        name: template.name,
        description: template.description,
        items: template.items || [],
        createdAt: new Date(template.createdAt),
        createdBy: template.createdBy,
        isPublic: template.isPublic,
        usageCount: template.usageCount
      }));
    } catch (error) {
      console.error('Errore nel recupero dei template:', error);
      throw error;
    }
  },
  
  /**
   * Applica un template a un task
   */
  applyTemplate: async (templateData: ApplyChecklistTemplateDto): Promise<void> => {
    try {
      await axiosInstance.post(`${API_URL}/apply-template`, templateData);
    } catch (error) {
      console.error('Errore nell\'applicazione del template:', error);
      throw error;
    }
  },
  
  // ================== UTILITY FUNCTIONS ==================
  
  /**
   * Calcola la percentuale di completamento per un array di elementi
   */
  calculateCompletionPercentage: (items: ChecklistItemDto[]): number => {
    if (!items.length) return 0;
    const completedCount = items.filter(item => item.isCompleted).length;
    return Math.round((completedCount / items.length) * 100);
  },
  
  /**
   * Filtra elementi per stato
   */
  filterByStatus: (items: ChecklistItemDto[], status: 'all' | 'completed' | 'pending' | 'overdue'): ChecklistItemDto[] => {
    switch (status) {
      case 'completed':
        return items.filter(item => item.isCompleted);
      case 'pending':
        return items.filter(item => !item.isCompleted && !item.isOverdue);
      case 'overdue':
        return items.filter(item => !item.isCompleted && item.isOverdue);
      default:
        return items;
    }
  },
  
  /**
   * Ordina elementi per criterio specificato
   */
  sortItems: (items: ChecklistItemDto[], sortBy: 'order' | 'title' | 'dueDate' | 'createdAt', sortOrder: 'asc' | 'desc'): ChecklistItemDto[] => {
    return [...items].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'order':
          comparison = a.order - b.order;
          break;
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'dueDate':
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          comparison = a.dueDate.getTime() - b.dueDate.getTime();
          break;
        case 'createdAt':
          comparison = a.createdAt.getTime() - b.createdAt.getTime();
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }
};

export default checklistItemService;